import type { CurrentUser, EmailMetadata } from "./types";
import { app } from "@microsoft/teams-js";

const BODY_SNIPPET_LENGTH = 700;
const BODY_FULL_LENGTH = 12000;
const HOST_TIMEOUT_MS = 6000;

export type HostPlatform = "outlook" | "teams";

export interface HostContext {
  platform: HostPlatform;
  user: CurrentUser;
}

function withTimeout<T>(promise: Promise<T>, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      reject(new Error(message));
    }, HOST_TIMEOUT_MS);

    promise
      .then((value) => {
        window.clearTimeout(timeout);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeout);
        reject(error);
      });
  });
}

function emailAddressToText(address?: Office.EmailAddressDetails): string {
  if (!address) {
    return "";
  }

  const name = address.displayName || "";
  const email = address.emailAddress || "";
  return name && email ? `${name} <${email}>` : email || name;
}

function isEmailAddress(value?: string): value is string {
  return typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function firstEmailAddress(...values: Array<string | undefined>): string {
  const email = values.find(isEmailAddress);
  return email ? email.trim().toLowerCase() : "";
}

function trimBody(text: string, limit: number): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  return normalized.length > limit ? `${normalized.slice(0, limit).trim()}...` : normalized;
}

function getBodyText(item: Office.MessageRead): Promise<string> {
  return new Promise((resolve) => {
    item.body.getAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value || "");
        return;
      }

      resolve("");
    });
  });
}

function getInternetMessageId(item: Office.MessageRead): string {
  const maybeItem = item as Office.MessageRead & { internetMessageId?: string };
  return maybeItem.internetMessageId || item.itemId || "";
}

export async function waitForOffice(): Promise<"outlook" | "office" | "web"> {
  if (typeof Office === "undefined") {
    return "web";
  }

  return new Promise((resolve) => {
    Office.onReady((info) => {
      resolve(info.host === Office.HostType.Outlook ? "outlook" : "office");
    });
  });
}

async function readTeamsHostContext(): Promise<HostContext> {
  console.log("Starting Teams initialization...");
  try {
    // Teams SDK v2 initialization
    console.log("Calling app.initialize()...");
    await withTimeout(app.initialize(), "Microsoft Teams initialization timed out.");
    console.log("app.initialize() succeeded.");
    
    // Get context using the newer Promise-based API
    console.log("Calling app.getContext()...");
    const context = await withTimeout(app.getContext(), "Microsoft Teams context could not be retrieved.");
    console.log("app.getContext() succeeded:", JSON.stringify(context.user || {}));
    const user = context.user;

    return {
      platform: "teams",
      user: {
        email: firstEmailAddress(user?.userPrincipalName, user?.loginHint),
        name: user?.displayName || ""
      }
    };
  } catch (error) {
    console.error("Teams initialization failed:", error);
    throw new Error(error instanceof Error ? error.message : "Open this app from within Microsoft Teams.");
  }
}

async function readOutlookHostContext(): Promise<HostContext> {
  const platform = await waitForOffice();

  if (platform !== "outlook") {
    throw new Error("Open this app from Microsoft Outlook or Microsoft Teams.");
  }

  const userProfile = Office.context?.mailbox?.userProfile;

  return {
    platform: "outlook",
    user: {
      email: userProfile?.emailAddress || "",
      name: userProfile?.displayName || ""
    }
  };
}

export async function readHostContext(preferredPlatform: string): Promise<HostContext> {
  if (preferredPlatform === "teams") {
    return readTeamsHostContext();
  }

  return readOutlookHostContext();
}

export async function readCurrentEmail(): Promise<EmailMetadata | null> {
  await waitForOffice();

  const item = Office.context?.mailbox?.item as Office.MessageRead | undefined;

  if (!item) {
    return null;
  }

  const body = await getBodyText(item);
  const from = item.from;
  const recipients = [...(item.to || []), ...(item.cc || [])].map(emailAddressToText).filter(Boolean);

  return {
    subject: item.subject || "",
    fromName: from?.displayName || "",
    fromEmail: from?.emailAddress || "",
    recipients,
    receivedDateTime: item.dateTimeCreated ? item.dateTimeCreated.toISOString() : "",
    bodySnippet: trimBody(body, BODY_SNIPPET_LENGTH),
    bodyFull: trimBody(body, BODY_FULL_LENGTH),
    messageId: getInternetMessageId(item)
  };
}

export async function readCurrentUserInfo(): Promise<CurrentUser> {
  return (await readOutlookHostContext()).user;
}
